<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import Modal from '../Modal.vue';
import ReviveSubscriptionOverlay from '../ReviveSubscriptionOverlay.vue';
import Cancel from '../Cancel.vue';
import Pause from '../Pause.vue';
import InfoCard from '../InfoCard.vue';
import { SubscriptionActionType } from '../../constants';
import { getTaxedPrice } from '../../utils/membershipHelpers';
import { formatUnixToMonthYear } from '../../utils/helpers';

export default {
  name: 'MembershipStatusCard',
  components: {
    Modal, ReviveSubscriptionOverlay, Cancel, Pause, InfoCard,
  },
  setup() {
    const store = useStore();
    const sub = computed(() => store.getters.currentSubscriptions?.[0]);
    const hasOwnership = computed(() => store.getters.hasOwnership);
    const isAnnual = computed(() => sub.value?.plan?.interval === 'year' || store.getters.isAnnualCustomer);

    const isCanceled = computed(() => !!sub.value?.cancel_at_period_end || sub.value?.status === 'canceled');
    const isPaused = computed(() => !!sub.value?.pause_collection);
    const isActive = computed(() => !isCanceled.value && !isPaused.value && sub.value?.status !== 'canceled');

    const statusText = computed(() => {
      if (sub.value?.status === 'canceled') return 'Canceled';
      if (isCanceled.value) return 'Canceling';
      if (isPaused.value) return 'Paused';
      const s = sub.value?.status;
      if (s === 'past_due') return 'Past Due';
      if (s === 'trialing') return 'Trialing';
      return 'Active';
    });

    const statusBadgeClass = computed(() => {
      if (isCanceled.value || sub.value?.status === 'past_due') return 'membership-status-card__badge--warning';
      if (isPaused.value) return 'membership-status-card__badge--paused';
      return 'membership-status-card__badge--active';
    });

    const billingInterval = computed(() => (isAnnual.value ? 'Billed Annually' : 'Billed Monthly'));

    const price = computed(() => {
      if (!sub.value) return '—';
      return getTaxedPrice(sub.value);
    });

    const priceInterval = computed(() => (isAnnual.value ? 'yr' : 'mo'));

    const restartActionType = computed(() => {
      if (sub.value?.status === 'canceled') return SubscriptionActionType.CREATE;
      if (isPaused.value) return SubscriptionActionType.RESUME;
      return SubscriptionActionType.REACTIVATE;
    });

    const nextBillDate = computed(() => {
      if (!sub.value?.current_period_end || sub.value?.status === 'canceled') return null;
      return formatUnixToMonthYear(sub.value.current_period_end);
    });

    return {
      sub,
      hasOwnership,
      isCanceled,
      isPaused,
      isActive,
      statusText,
      statusBadgeClass,
      billingInterval,
      price,
      priceInterval,
      nextBillDate,
      restartActionType,
      SubscriptionActionType,
    };
  },
};
</script>

<template>
  <InfoCard v-if="sub" title="Membership Status">
    <div class="membership-status-card__meta">
      <div class="membership-status-card__status-row">
        <span class="membership-status-card__badge" :class="statusBadgeClass">
          <span class="membership-status-card__dot"></span>
          {{ statusText }}
        </span>
        <span class="membership-status-card__interval">{{ billingInterval }}</span>
      </div>

      <div class="membership-status-card__pricing-row">
        <span class="membership-status-card__price">{{ price }}</span>
        <span class="membership-status-card__price-interval">/{{ priceInterval }}</span>
      </div>

      <p v-if="nextBillDate" class="membership-status-card__next-bill">
        Next bill: {{ nextBillDate }}
      </p>
    </div>

    <div v-if="hasOwnership" class="membership-status-card__actions">
      <!-- Fully canceled: navigate to plan selection page -->
      <router-link
        v-if="isCanceled && restartActionType === SubscriptionActionType.CREATE"
        :to="{ name: 'RestartMembership' }"
        class="membership-status-card__btn button-layout button-cta"
      >
        Restart Membership
      </router-link>

      <!-- Soft-canceled or paused: modal is sufficient -->
      <Modal v-else-if="isCanceled || isPaused">
        <template #trigger="{ showModal }">
          <button
            class="membership-status-card__btn button-layout button-cta"
            @click="showModal()"
          >
            Restart Membership
          </button>
        </template>
        <template #modal-content="{ hideModal }">
          <ReviveSubscriptionOverlay
            :subId="sub.id"
            :actionType="restartActionType"
            :isCancelClicked="false"
            @closeModal="hideModal()"
          />
        </template>
      </Modal>

      <template v-if="isActive">
        <Modal>
          <template #trigger="{ showModal }">
            <button
              class="membership-status-card__btn button-layout button-outline"
              @click="showModal()"
            >
              Pause
            </button>
          </template>
          <template #modal-content="{ hideModal }">
            <Pause
              :subId="sub.id"
              @closeModal="hideModal()"
            />
          </template>
        </Modal>

        <Modal>
          <template #trigger="{ showModal }">
            <button
              class="membership-status-card__btn button-layout button-outline"
              @click="showModal()"
            >
              Cancel
            </button>
          </template>
          <template #modal-content="{ hideModal }">
            <Cancel
              :subId="sub.id"
              :actionType="SubscriptionActionType.CANCEL"
              :isCancelClicked="false"
              @closeModal="hideModal()"
            />
          </template>
        </Modal>
        <p class="membership-status-card__members">If you wish to switch between tiers, please contact customer support.</p>
      </template>

    </div>
  </InfoCard>
</template>

<style lang="scss" scoped>
.membership-status-card {
  &__meta {
    flex: 1;
    margin-bottom: 1.5rem;
  }

  &__status-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.7rem;
    font-family: $font-stack-heading;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;

    &--active {
      background-color: rgba($color-system-green, 0.12);
      color: $color-system-green;

      .membership-status-card__dot {
        background-color: $color-system-green;
      }
    }

    &--warning {
      background-color: rgba($color-red, 0.1);
      color: $color-red;

      .membership-status-card__dot {
        background-color: $color-red;
      }
    }

    &--paused {
      background-color: rgba($fc-greyscale-600, 0.15);
      color: $fc-greyscale-700;

      .membership-status-card__dot {
        background-color: $fc-greyscale-600;
      }
    }
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__interval {
    font-size: 0.8rem;
    color: $fc-greyscale-700;
  }

  &__pricing-row {
    display: flex;
    align-items: baseline;
    gap: 0.15rem;
    margin-bottom: 0.5rem;
  }

  &__price {
    font-family: $font-stack-heading;
    font-size: 2.5rem;
    color: $color-black;
    line-height: 1;
  }

  &__price-interval {
    font-size: 0.85rem;
    color: $fc-greyscale-700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__next-bill {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $fc-greyscale-700;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__btn {
    flex: 1;

    &:disabled {
      opacity: 0.4; cursor: not-allowed;
    }

  }

  &__members {
    font-size: 0.75rem;
    color: $fc-greyscale-700;
    margin-top: 0.5rem;
  }
}
</style>
